import {
  Conversation_Kind,
  type Message as WireMessage,
  type Content as WireContent,
} from '@gen/chat/v1/common_pb';
import { DeleteMessageRequest_Scope, MethodId } from '@gen/chat/v1/methods_p1_pb';
import { MethodIdP2 } from '@gen/chat/v1/methods_p2_pb';
import { MethodIdP3 } from '@gen/chat/v1/methods_p3_pb';
import { MethodIdP4 } from '@gen/chat/v1/methods_p4_pb';
import { payloadHash } from '@/protocol/canonical';
import { LocalPersistenceError } from '@/protocol/errors';
import { MAX_REACTIONS_PER_USER } from '@/protocol/limits';
import {
  attachmentContentKind,
  normalizeClosePoll,
  normalizeCreateChannel,
  normalizeCreateDirectConversation,
  normalizeCreateGroup,
  normalizeCreateInvite,
  normalizeCreatePoll,
  normalizeDeleteMessage,
  normalizeDeleteTopic,
  normalizeEditMessage,
  normalizeForwardMessage,
  normalizeJoinConversation,
  normalizeLeaveConversation,
  normalizeLinkDiscussion,
  normalizeMarkRead,
  normalizePublishPost,
  normalizeResolveJoinRequest,
  normalizeRevokeInvite,
  normalizeSendMessage,
  normalizeSetBan,
  normalizeSetConversationPolicy,
  normalizeSetDraft,
  normalizeSetMemberRole,
  normalizeSetPin,
  normalizeSetPlaybackPosition,
  normalizeSetReaction,
  normalizeSubscribeChannel,
  normalizeUnsubscribeChannel,
  normalizeUpdateConversation,
  normalizeUpsertTopic,
  normalizeVote,
  textContent,
} from '@/protocol/operations';
import { db } from '@/store/db';
import { bytesToHex, newOperationId, newTempId, newUuid, randomBytes, type Hex } from '@/store/ids';
import { isQuotaExceeded, type StorageTrouble } from '@/store/quota';
import {
  PENDING_ORDER_BASE,
  type ConversationRecord,
  type DraftRecord,
  type MediaRecord,
  type MemberRecord,
  type MessageRecord,
  type NormalizedPayload,
  type NotificationMode,
  type OutboxRecord,
  type PinRecord,
  type PollRecord,
  type ReactionRecord,
  type StoredAttachment,
  type StoredContent,
  type StoredSender,
  type TopicRecord,
  type UploadRecord,
} from '@/store/types';

/**
 * LocalStore: everything the interface reads and everything a user action
 * writes.
 *
 * The rule that shapes this whole module is the Section 05 invariant: the
 * operation and its optimistic message are persisted in ONE transaction before
 * any network attempt. Every enqueue below therefore does all of its hashing
 * and identifier generation first, outside the transaction, and then performs a
 * single Dexie `rw` transaction whose failure means nothing was written at all.
 * A partially written send — an outbox row with no bubble, or a bubble with no
 * outbox row — is the one outcome that must be impossible, because the first
 * loses the user's message silently and the second shows a message that will
 * never be sent.
 *
 * Network-attempt state (`state`, `attemptCount`, `nextAttemptAt`,
 * `lastAttemptAt`, `lastError`) is written by the OutboxScheduler and by
 * nothing else. Those writes live in `store/outboxWrites.ts`.
 */

// ---------------------------------------------------------------------------
// Storage health
// ---------------------------------------------------------------------------

type StorageListener = (trouble: StorageTrouble) => void;

const trouble: StorageTrouble = { quotaExceeded: false, unavailable: false };
const storageListeners = new Set<StorageListener>();

export function onStorageTrouble(listener: StorageListener): () => void {
  storageListeners.add(listener);
  listener({ ...trouble });
  return () => {
    storageListeners.delete(listener);
  };
}

function publishTrouble(): void {
  for (const listener of storageListeners) {
    listener({ ...trouble });
  }
}

/**
 * Classifies a storage failure so the interface can say something true.
 *
 * A quota failure and a database that will not open are different situations
 * with different consequences — one loses new work, the other loses all offline
 * capability — and Section 05 requires both to be reported rather than hidden.
 */
function recordStorageFailure(error: unknown): void {
  if (isQuotaExceeded(error)) {
    if (!trouble.quotaExceeded) {
      trouble.quotaExceeded = true;
      publishTrouble();
    }
    return;
  }
  if (isDatabaseUnavailable(error) && !trouble.unavailable) {
    trouble.unavailable = true;
    publishTrouble();
  }
}

/**
 * True when IndexedDB itself refused, rather than one write failing.
 *
 * A private window with site data blocked throws on open, and Dexie surfaces
 * that as one of these names. Treating it as a transient write failure would
 * leave the interface silently pretending it has an offline store.
 */
function isDatabaseUnavailable(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }
  const name = (error as { name?: unknown }).name;
  return (
    name === 'InvalidStateError' ||
    name === 'SecurityError' ||
    name === 'UnknownError' ||
    name === 'DatabaseClosedError' ||
    name === 'MissingAPIError'
  );
}

/**
 * Runs a write and converts any failure into the one error the interface knows
 * how to present honestly.
 */
async function persist<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    recordStorageFailure(error);
    throw new LocalPersistenceError(
      'the local database rejected the write',
      isQuotaExceeded(error),
      { cause: error },
    );
  }
}

// ---------------------------------------------------------------------------
// Preferences and account
// ---------------------------------------------------------------------------

const INSTALLATION_KEY = 'installation_id';

/**
 * The installation identifier the identity service binds a device row to.
 *
 * It lives in IndexedDB rather than local storage because the whole point is
 * that it survives a sign-out and identifies *this browser profile*; local
 * storage is the store a user clears first and the one a private window
 * discards, and a rotating installation identifier would make the server treat
 * every reconnection as a new device with no claim on pending operations.
 */
export async function ensureInstallationId(): Promise<string> {
  try {
    const existing = await db.preferences.get(INSTALLATION_KEY);
    if (existing && typeof existing.value === 'string') {
      return existing.value;
    }
    const created = newUuid();
    await db.preferences.put({ key: INSTALLATION_KEY, value: created });
    return created;
  } catch (error) {
    // A browser that will not give this origin a database still deserves a
    // working live session. The identifier becomes per-tab rather than per
    // installation, the storage notice explains that offline history is
    // unavailable, and nothing here pretends the write succeeded.
    recordStorageFailure(error);
    return newUuid();
  }
}

export async function getSignedInAccount() {
  const accounts = await db.accounts.toArray();
  return accounts[0] ?? null;
}

// ---------------------------------------------------------------------------
// Conversations, messages and drafts — read paths
// ---------------------------------------------------------------------------

export async function listConversations(): Promise<ConversationRecord[]> {
  // Newest activity first; `updatedAt` is indexed so this is a reverse scan
  // rather than a sort of the whole store.
  return db.conversations.orderBy('updatedAt').reverse().toArray();
}

export async function getConversation(conversationId: Hex): Promise<ConversationRecord | null> {
  return (await db.conversations.get(conversationId)) ?? null;
}

/**
 * The identifier a provisional conversation was renamed to, if it was.
 *
 * A conversation created locally is opened under a provisional identifier and
 * re-keyed when the server names the real one. Anything still holding the old
 * identifier resolves it through here rather than concluding the conversation
 * does not exist.
 */
export async function resolveConversationAlias(conversationId: Hex): Promise<Hex | null> {
  const alias = await db.conversationAliases.get(conversationId);
  return alias?.canonicalId ?? null;
}

/**
 * The visible window of a conversation, oldest-first.
 *
 * Section 14 opens with the 50 latest messages and keyset-loads the previous 50
 * near 500 px from the top. Both are this one query with a larger `limit`: it
 * is a keyset scan backwards along `[conversationId+orderSeq]`, so extending
 * the window costs one more index range and never an offset, which would shift
 * under concurrent inserts and duplicate or skip a message exactly when new
 * ones are arriving.
 *
 * The returned window necessarily grows as the reader pages back, because
 * everything on screen has to be in it. What stays bounded is *rendering*: the
 * virtualizer draws about thirty rows regardless of the window size, which is
 * what the Section 14 low-memory gate actually asks for.
 */
export async function loadLatestMessages(
  conversationId: Hex,
  limit: number,
): Promise<MessageRecord[]> {
  const page = await db.messages
    .where('[conversationId+orderSeq]')
    .between([conversationId, 0], [conversationId, Number.MAX_SAFE_INTEGER], true, true)
    .reverse()
    .limit(limit)
    .toArray();
  return page.reverse();
}

export async function getMessage(
  conversationId: Hex,
  messageId: string,
): Promise<MessageRecord | null> {
  return (await db.messages.get([conversationId, messageId])) ?? null;
}

export async function getDraft(conversationId: Hex): Promise<DraftRecord | null> {
  return (await db.drafts.get(conversationId)) ?? null;
}

export async function listDrafts(): Promise<DraftRecord[]> {
  return db.drafts.toArray();
}

export async function saveDraft(draft: DraftRecord): Promise<void> {
  await persist(() => db.drafts.put(draft));
}

export async function deleteDraft(conversationId: Hex): Promise<void> {
  await persist(() => db.drafts.delete(conversationId));
}

/**
 * The queued operations for one conversation.
 *
 * Scoped to a conversation rather than the whole account because the timeline
 * is the only reader: a message bubble needs its own operation's state and
 * nothing else, and a whole-account query would re-run on every unrelated send.
 */
export async function listOutboxForConversation(conversationId: Hex): Promise<OutboxRecord[]> {
  return db.outbox
    .where('[conversationId+createdAt]')
    .between([conversationId, 0], [conversationId, Number.MAX_SAFE_INTEGER], true, true)
    .toArray();
}

export async function countUnsentOperations(): Promise<number> {
  return db.outbox.where('state').anyOf('QUEUED', 'SENDING', 'FAILED').count();
}

// ---------------------------------------------------------------------------
// Wire conversions
// ---------------------------------------------------------------------------

export function emptyContent(): StoredContent {
  return {
    kind: 0,
    text: '',
    entities: [],
    attachments: [],
    replyTo: null,
    topicId: null,
    embeddedId: null,
  };
}

export function contentFromWire(content: WireContent | undefined): StoredContent {
  if (!content) {
    return emptyContent();
  }
  return {
    kind: content.kind,
    text: content.text,
    entities: content.entities.map((entity) => ({
      kind: entity.kind,
      offset: entity.offset,
      length: entity.length,
      value: entity.value ?? null,
    })),
    attachments: content.attachments.map((attachment) => ({
      mediaId: bytesToHex(attachment.mediaId),
      caption: attachment.caption ?? null,
      filename: attachment.filename ?? null,
      mime: attachment.mime,
      sizeBytes: Number(attachment.sizeBytes),
      width: attachment.width ?? null,
      height: attachment.height ?? null,
      durationMs: attachment.durationMs ?? null,
    })),
    replyTo: content.replyTo ? bytesToHex(content.replyTo) : null,
    topicId: content.topicId ? bytesToHex(content.topicId) : null,
    embeddedId: content.embeddedId ? bytesToHex(content.embeddedId) : null,
  };
}

/**
 * Reads the typed sender.
 *
 * The subject is a discriminated union on the wire, so the kind travels with the
 * identifier and a message cannot describe itself as both a user and a channel.
 * Returns null only for a message from a server that predates the typed field.
 */
function senderFromWire(message: WireMessage): StoredSender | null {
  const sender = message.sender;
  if (sender === undefined) return null;

  const subject = sender.subject;
  if (subject.case === 'channelId') {
    return {
      kind: 'CHANNEL',
      id: bytesToHex(subject.value),
      displayName: sender.displayName,
      avatarMediaId: sender.avatarMediaId ? bytesToHex(sender.avatarMediaId) : null,
      profileVersion: Number(sender.profileVersion),
    };
  }
  if (subject.case === 'userId') {
    return {
      kind: 'USER',
      id: bytesToHex(subject.value),
      displayName: sender.displayName,
      avatarMediaId: sender.avatarMediaId ? bytesToHex(sender.avatarMediaId) : null,
      profileVersion: Number(sender.profileVersion),
    };
  }
  return null;
}

export function messageFromWire(message: WireMessage, ownUserId: Hex): MessageRecord {
  const sender = senderFromWire(message);
  // The typed sender is authoritative. sender_id is the deprecated 1.0 field and
  // is empty for a channel-origin message, so it is only a fallback for a user
  // message from a server that has not been upgraded.
  const senderId =
    sender?.kind === 'USER'
      ? sender.id
      : sender?.kind === 'CHANNEL'
        ? null
        : message.senderId.length > 0
          ? bytesToHex(message.senderId)
          : null;
  const origin = message.forwardOrigin;
  return {
    conversationId: bytesToHex(message.conversationId),
    id: bytesToHex(message.id),
    orderSeq: Number(message.createdSeq),
    createdSeq: Number(message.createdSeq),
    senderId,
    sender,
    version: Number(message.version),
    content: contentFromWire(message.content),
    createdAt: Number(message.createdAt),
    editedAt: message.editedAt === undefined ? null : Number(message.editedAt),
    deleted: message.deleted,
    // A channel post is never "mine", even when this account published it: it is
    // displayed as the channel, and showing it as an outgoing message would put
    // the administrator back into the interface the server just removed them
    // from.
    outgoing: sender?.kind !== 'CHANNEL' && senderId === ownUserId,
    localState: 'COMMITTED',
    opId: null,
    tempId: null,
    // Section 06: origin attribution is preserved only when the origin permits
    // it. The server has already applied that rule — an origin it chose not to
    // disclose simply is not in the message — so the client copies what it is
    // given and never reconstructs identifiers from anywhere else.
    forwardOrigin: origin
      ? {
          conversationId: origin.conversationId ? bytesToHex(origin.conversationId) : null,
          messageId: origin.messageId ? bytesToHex(origin.messageId) : null,
          displayName: origin.displayName,
        }
      : null,
    viewCount: 0,
    pinned: false,
  };
}

// ---------------------------------------------------------------------------
// Enqueue — the single-transaction invariant
// ---------------------------------------------------------------------------

export interface EnqueuedOperation {
  opId: Hex;
  tempId: string;
}

interface EnqueueContext {
  conversationId: Hex;
  originDeviceId: Hex;
  methodId: number;
  normalizedPayload: NormalizedPayload;
  /** Attachment metadata for a content-carrying operation; not hashed. */
  attachments?: StoredAttachment[];
}

/**
 * Writes an outbox record together with every local change that belongs to the
 * same user action.
 *
 * This is the Section 05 invariant in code: "persist the new operation and
 * optimistic message in one transaction before attempting the network". The
 * `apply` callback runs inside that transaction — Dexie operations issued from
 * it join the ambient transaction — so an optimistic bubble, a cleared draft, a
 * struck-through message and the operation that will make any of them real
 * either all exist or none do.
 *
 * Everything expensive or fallible that does not belong inside a transaction —
 * hashing, identifier generation — happens before the transaction opens. Dexie
 * transactions cannot await a foreign promise without leaving their own scope,
 * and a hash computed inside a write transaction would hold locks on the
 * message store while the user is typing.
 */
async function enqueue(
  context: EnqueueContext,
  apply?: (opId: Hex, tempId: string, now: number) => Promise<void>,
): Promise<EnqueuedOperation> {
  const opId = newOperationId();
  const tempId = newTempId();
  const hash = await payloadHash(context.normalizedPayload);
  const now = Date.now();

  const outboxRecord: OutboxRecord = {
    opId,
    originDeviceId: context.originDeviceId,
    conversationId: context.conversationId,
    methodId: context.methodId,
    normalizedPayload: context.normalizedPayload,
    payloadHash: hash,
    attachments: context.attachments ?? [],
    tempId,
    state: 'QUEUED',
    attemptCount: 0,
    // Due immediately. The scheduler, not this write, decides when it is sent.
    nextAttemptAt: now,
    lastError: null,
    createdAt: now,
    lastAttemptAt: null,
  };

  await persist(async () => {
    await db.dexie.transaction(
      'rw',
      [
        db.outbox,
        db.messages,
        db.conversations,
        db.drafts,
        db.reactions,
        db.pins,
        db.polls,
        db.topics,
        db.members,
        db.invites,
        db.uploads,
      ],
      async () => {
        // `add` rather than `put`: an operation identifier collision would mean
        // the generator is broken, and overwriting an existing operation would
        // destroy work rather than surface the bug.
        await db.outbox.add(outboxRecord);
        if (apply) {
          await apply(opId, tempId, now);
        }
      },
    );
  });

  return { opId, tempId };
}

export interface SendMessageInput {
  conversationId: Hex;
  senderId: Hex;
  originDeviceId: Hex;
  text: string;
  replyTo: Hex | null;
  /** Group topic, when the conversation uses them. */
  topicId?: Hex | null;
  /** READY attachments, in the order the user arranged them. */
  attachments?: StoredAttachment[];
  /** Media kind per attachment, which decides the content kind. */
  attachmentKinds?: string[];
  /** Text entities: mentions, links and formatting. */
  entities?: StoredContent['entities'];
}

export async function enqueueSendMessage(input: SendMessageInput): Promise<EnqueuedOperation> {
  const attachments = input.attachments ?? [];
  const content: StoredContent = {
    ...textContent(input.text, input.replyTo, input.topicId ?? null),
    ...(attachments.length > 0
      ? { kind: attachmentContentKind(input.attachmentKinds ?? []), attachments }
      : {}),
    entities: input.entities ?? [],
  };

  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodId.SEND_MESSAGE,
      normalizedPayload: normalizeSendMessage(input.conversationId, content),
      attachments,
    },
    async (opId, tempId, now) => {
      const message: MessageRecord = {
        conversationId: input.conversationId,
        id: tempId,
        // Pending messages sort after everything the server has sequenced, and
        // the millisecond timestamp keeps several pending sends in send order.
        orderSeq: PENDING_ORDER_BASE + now,
        createdSeq: 0,
        senderId: input.senderId,
        sender: { kind: 'USER' as const, id: input.senderId, displayName: '', avatarMediaId: null, profileVersion: 0 },
        version: 1,
        content,
        createdAt: now,
        editedAt: null,
        deleted: false,
        outgoing: true,
        localState: 'PENDING',
        opId,
        tempId,
        forwardOrigin: null,
        viewCount: 0,
        pinned: false,
      };
      await db.messages.add(message);

      const conversation = await db.conversations.get(input.conversationId);
      if (conversation) {
        await db.conversations.put({
          ...conversation,
          updatedAt: now,
          lastMessageAt: now,
          lastMessagePreview: content.text,
          // Advisory local slow-mode clock; the server enforces the real limit.
          lastOwnSendAt: now,
        });
      }

      // The draft has become the message; Section 14 keeps reply context
      // attached to the draft only until it is removed or successfully sent.
      await db.drafts.delete(input.conversationId);
    },
  );
}

export interface EditMessageInput {
  conversationId: Hex;
  messageId: Hex;
  originDeviceId: Hex;
  expectedVersion: number;
  text: string;
}

export async function enqueueEditMessage(input: EditMessageInput): Promise<EnqueuedOperation> {
  const existing = await getMessage(input.conversationId, input.messageId);
  // An edit keeps the message's attachments and topic: Section 06 lets an
  // author change text, not re-target or re-attach a committed message.
  const base = textContent(input.text, existing?.content.replyTo ?? null, existing?.content.topicId ?? null);
  const content: StoredContent = existing
    ? {
        ...base,
        kind: existing.content.kind,
        attachments: existing.content.attachments,
        embeddedId: existing.content.embeddedId,
      }
    : base;

  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodId.EDIT_MESSAGE,
      normalizedPayload: normalizeEditMessage(
        input.conversationId,
        input.messageId,
        input.expectedVersion,
        content,
      ),
      attachments: content.attachments,
    },
    async (_opId, _tempId, now) => {
      const message = await db.messages.get([input.conversationId, input.messageId]);
      if (!message) {
        return;
      }
      // The edit shows immediately on the existing bubble. The version is not
      // advanced locally: the server owns versions, and inventing one here
      // would make the next edit send an expected_version the server never
      // issued, which it would correctly reject as a conflict.
      await db.messages.put({ ...message, content, editedAt: now });
    },
  );
}

export interface DeleteMessageInput {
  conversationId: Hex;
  messageId: Hex;
  originDeviceId: Hex;
  expectedVersion: number;
  scope: DeleteMessageRequest_Scope;
}

export async function enqueueDeleteMessage(input: DeleteMessageInput): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodId.DELETE_MESSAGE,
      normalizedPayload: normalizeDeleteMessage(
        input.conversationId,
        input.messageId,
        input.scope,
        input.expectedVersion,
      ),
    },
    async () => {
      const message = await db.messages.get([input.conversationId, input.messageId]);
      if (message) {
        await db.messages.put({ ...message, deleted: true });
      }
    },
  );
}

export interface CreateDirectConversationInput {
  peerId: Hex;
  peerTitle: string;
  originDeviceId: Hex;
}

/**
 * Queues a direct-conversation creation and gives it a provisional local
 * identifier immediately.
 *
 * The provisional identifier is what makes the rest of the client uniform: the
 * user can open the conversation and queue messages into it while offline, and
 * every one of those operations carries a conversation identifier that
 * per-conversation FIFO ordering can key on. Reconciliation rewrites the rows
 * to the canonical identifier the server returns.
 */
export async function enqueueCreateDirectConversation(
  input: CreateDirectConversationInput,
): Promise<EnqueuedOperation & { conversationId: Hex }> {
  const provisionalId = bytesToHex(randomBytes(16));

  const enqueued = await enqueue(
    {
      conversationId: provisionalId,
      originDeviceId: input.originDeviceId,
      methodId: MethodId.CREATE_DIRECT_CONVERSATION,
      normalizedPayload: normalizeCreateDirectConversation(input.peerId),
    },
    async (_opId, _tempId, now) => {
      await db.conversations.put(
        // A direct conversation has no role: both participants are peers, and
        // an invented "OWNER" would make the group-info controls appear.
        provisionalConversation(provisionalId, Conversation_Kind.DIRECT, input.peerTitle, now, ''),
      );
    },
  );

  return { ...enqueued, conversationId: provisionalId };
}

/**
 * A conversation row that stands in for one the server has not created yet.
 *
 * Shared by direct conversations, groups and channels so that a new field added
 * to `ConversationRecord` cannot be filled in three places and forgotten in a
 * fourth.
 */
function provisionalConversation(
  id: Hex,
  kind: number,
  title: string,
  now: number,
  ownRole = 'OWNER',
): ConversationRecord {
  return {
    id,
    kind,
    title,
    headSeq: 0,
    lastReadSeq: 0,
    peerReadSeq: 0,
    peerDeliveredSeq: 0,
    unreadCount: 0,
    unreadCapped: false,
    policyVersion: 0,
    historyPolicy: 0,
    deliveryMode: 0,
    ownRole,
    updatedAt: now,
    lastMessagePreview: '',
    lastMessageAt: now,
    provisional: true,
    accessRevoked: false,
    slowModeSeconds: 0,
    protectedContent: false,
    publicUsername: null,
    linkedDiscussionId: null,
    photoMediaId: null,
    notificationMode: 'MENTIONS',
    subscribed: false,
    lastOwnSendAt: 0,
  };
}

export interface MarkReadInput {
  conversationId: Hex;
  originDeviceId: Hex;
  lastReadCreatedSeq: number;
}

export async function enqueueMarkRead(input: MarkReadInput): Promise<EnqueuedOperation | null> {
  const conversation = await getConversation(input.conversationId);
  // Read state is monotonic. Sending a position the account has already passed
  // would be a no-op on the server and a wasted mutation slot on a weak
  // network, so it is dropped here.
  if (!conversation || conversation.lastReadSeq >= input.lastReadCreatedSeq) {
    return null;
  }

  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodId.MARK_READ,
      normalizedPayload: normalizeMarkRead(input.conversationId, input.lastReadCreatedSeq),
    },
    async () => {
      const current = await db.conversations.get(input.conversationId);
      if (current && current.lastReadSeq < input.lastReadCreatedSeq) {
        await db.conversations.put({
          ...current,
          lastReadSeq: input.lastReadCreatedSeq,
          unreadCount: 0,
          unreadCapped: false,
        });
      }
    },
  );
}
// ---------------------------------------------------------------------------
// Phase 2 — reactions, pins, forwarding, drafts, playback
// ---------------------------------------------------------------------------

export async function listReactions(conversationId: Hex): Promise<ReactionRecord[]> {
  return db.reactions.where('[conversationId+messageId]')
    .between([conversationId, ''], [conversationId, '￿'], true, true)
    .toArray();
}

export interface SetReactionInput {
  conversationId: Hex;
  messageId: string;
  userId: Hex;
  originDeviceId: Hex;
  reaction: string;
  active: boolean;
}

/**
 * Adds or removes one reaction.
 *
 * Section 06 caps a user at three distinct reactions per message and makes
 * set/remove idempotent. Both are enforced here before anything is queued: an
 * idempotent no-op should not consume a mutation slot on a weak network, and a
 * fourth reaction should be refused where the user can see why rather than
 * silently accepted and then rejected by the server.
 */
export async function enqueueSetReaction(
  input: SetReactionInput,
): Promise<EnqueuedOperation | null> {
  const key: [string, string, string, string] = [
    input.conversationId,
    input.messageId,
    input.userId,
    input.reaction,
  ];
  const existing = await db.reactions.get(key);
  if ((existing?.active ?? false) === input.active) {
    return null;
  }
  if (input.active) {
    const mine = await db.reactions
      .where('[conversationId+messageId]')
      .equals([input.conversationId, input.messageId])
      .filter((record) => record.userId === input.userId && record.active)
      .count();
    if (mine >= MAX_REACTIONS_PER_USER) {
      return null;
    }
  }

  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP2.METHOD_ID_SET_REACTION,
      normalizedPayload: normalizeSetReaction(
        input.conversationId,
        input.messageId as Hex,
        input.reaction,
        input.active,
      ),
    },
    async () => {
      await db.reactions.put({
        conversationId: input.conversationId,
        messageId: input.messageId,
        userId: input.userId,
        reaction: input.reaction,
        active: input.active,
        // The version stays at the last server value. Inventing one would make
        // a later REACTION_CHANGED event look stale and be discarded.
        version: existing?.version ?? 0,
      });
    },
  );
}

export async function listPins(conversationId: Hex): Promise<PinRecord[]> {
  return db.pins.where('conversationId').equals(conversationId).toArray();
}

export async function enqueueSetPin(input: {
  conversationId: Hex;
  messageId: string;
  originDeviceId: Hex;
  pinned: boolean;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP2.METHOD_ID_SET_PIN,
      normalizedPayload: normalizeSetPin(
        input.conversationId,
        input.messageId as Hex,
        input.pinned,
      ),
    },
    async (_opId, _tempId, now) => {
      if (input.pinned) {
        await db.pins.put({
          conversationId: input.conversationId,
          messageId: input.messageId,
          pinnedAt: now,
        });
      } else {
        await db.pins.delete([input.conversationId, input.messageId]);
      }
      const message = await db.messages.get([input.conversationId, input.messageId]);
      if (message) {
        await db.messages.put({ ...message, pinned: input.pinned });
      }
    },
  );
}

export async function enqueueForwardMessage(input: {
  sourceConversationId: Hex;
  messageId: string;
  destinationConversationId: Hex;
  originDeviceId: Hex;
}): Promise<EnqueuedOperation> {
  // The operation belongs to the *destination* conversation for scheduling
  // purposes: Section 14's FIFO rule is about the order messages land in the
  // conversation they are sent to, and a forward is a send.
  return enqueue({
    conversationId: input.destinationConversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP2.METHOD_ID_FORWARD_MESSAGE,
    normalizedPayload: normalizeForwardMessage(
      input.sourceConversationId,
      input.messageId as Hex,
      input.destinationConversationId,
    ),
  });
}

export async function enqueueSetPlaybackPosition(input: {
  conversationId: Hex;
  messageId: string;
  originDeviceId: Hex;
  positionMs: number;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP2.METHOD_ID_SET_PLAYBACK_POSITION,
    normalizedPayload: normalizeSetPlaybackPosition(
      input.conversationId,
      input.messageId as Hex,
      Math.max(0, Math.round(input.positionMs)),
    ),
  });
}

/**
 * Synchronizes the local draft to the server.
 *
 * Section 06 fixes the timing at one second of idle and the concurrency control
 * at version checking. The expected version is the one the last accepted server
 * draft carried; a `VERSION_CONFLICT` comes back through the outbox as a
 * terminal error, and `recordDraftConflict` below is what turns that into the
 * choose-version action rather than an overwrite.
 */
export async function enqueueSetDraft(input: {
  conversationId: Hex;
  originDeviceId: Hex;
  draft: DraftRecord;
}): Promise<EnqueuedOperation> {
  const content = textContent(
    input.draft.text,
    input.draft.replyTo as Hex | null,
    input.draft.topicId,
  );
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP2.METHOD_ID_SET_DRAFT,
    normalizedPayload: normalizeSetDraft(
      input.conversationId,
      input.draft.version,
      content,
      input.draft.pendingUploadIds,
    ),
  });
}

/**
 * Records that the server refused a draft write because another device had
 * already moved the version on.
 *
 * Both copies survive. Section 06: "Conflicts retain both local and remote
 * copies and present a choose-version action."
 */
export async function recordDraftConflict(
  conversationId: Hex,
  remote: { text: string; version: number; updatedAt: number },
): Promise<void> {
  await persist(async () => {
    await db.dexie.transaction('rw', [db.drafts], async () => {
      const draft = await db.drafts.get(conversationId);
      if (!draft) {
        return;
      }
      await db.drafts.put({ ...draft, conflict: remote });
    });
  });
}

/** Keeps one side of a draft conflict and clears the marker. */
export async function resolveDraftConflict(
  conversationId: Hex,
  keep: 'local' | 'remote',
): Promise<void> {
  await persist(async () => {
    await db.dexie.transaction('rw', [db.drafts], async () => {
      const draft = await db.drafts.get(conversationId);
      if (!draft?.conflict) {
        return;
      }
      const remote = draft.conflict;
      await db.drafts.put(
        keep === 'remote'
          ? { ...draft, text: remote.text, version: remote.version, updatedAt: remote.updatedAt, conflict: null }
          : // Keeping the local text still adopts the remote *version*: the next
            // SetDraft has to be expressed against what the server currently
            // holds, or it would conflict again forever.
            { ...draft, version: remote.version, conflict: null },
      );
    });
  });
}

// ---------------------------------------------------------------------------
// Phase 2 — uploads and media, read paths
// ---------------------------------------------------------------------------

export async function listUploadsForConversation(conversationId: Hex): Promise<UploadRecord[]> {
  const rows = await db.uploads.where('conversationId').equals(conversationId).toArray();
  rows.sort((left, right) => left.createdAt - right.createdAt);
  return rows;
}

export async function getUpload(uploadId: Hex): Promise<UploadRecord | null> {
  return (await db.uploads.get(uploadId)) ?? null;
}

export async function getMedia(mediaId: Hex): Promise<MediaRecord | null> {
  return (await db.media.get(mediaId)) ?? null;
}

export async function listMediaFor(mediaIds: readonly Hex[]): Promise<MediaRecord[]> {
  if (mediaIds.length === 0) {
    return [];
  }
  const rows = await db.media.bulkGet([...mediaIds]);
  return rows.filter((row): row is MediaRecord => row !== undefined);
}

// ---------------------------------------------------------------------------
// Phase 3 — groups
// ---------------------------------------------------------------------------

export async function listMembers(conversationId: Hex): Promise<MemberRecord[]> {
  const rows = await db.members.where('conversationId').equals(conversationId).toArray();
  // Owner, then admins, then everyone else by name. `Member_Role` numbers
  // ascend in exactly that order, so the primary key is the enum value.
  rows.sort((left, right) =>
    left.role !== right.role
      ? left.role - right.role
      : left.displayName.localeCompare(right.displayName),
  );
  return rows;
}

export async function getMember(conversationId: Hex, userId: Hex): Promise<MemberRecord | null> {
  return (await db.members.get([conversationId, userId])) ?? null;
}

export async function listTopics(conversationId: Hex): Promise<TopicRecord[]> {
  const rows = await db.topics.where('conversationId').equals(conversationId).toArray();
  return rows.filter((topic) => !topic.deleted);
}

export async function getPoll(pollId: Hex): Promise<PollRecord | null> {
  return (await db.polls.get(pollId)) ?? null;
}

export async function listInvites(conversationId: Hex): Promise<import('@/store/types').InviteRecord[]> {
  return db.invites.where('conversationId').equals(conversationId).toArray();
}

export interface CreateGroupInput {
  title: string;
  initialMembers: Hex[];
  /** ALL_HISTORY or SINCE_JOIN, fixed at creation by Section 06. */
  historyPolicy: string;
  originDeviceId: Hex;
}

export async function enqueueCreateGroup(
  input: CreateGroupInput,
): Promise<EnqueuedOperation & { conversationId: Hex }> {
  const provisionalId = bytesToHex(randomBytes(16));
  const enqueued = await enqueue(
    {
      conversationId: provisionalId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP3.METHOD_ID_CREATE_GROUP,
      normalizedPayload: normalizeCreateGroup(
        input.title,
        input.initialMembers,
        input.historyPolicy,
      ),
    },
    async (_opId, _tempId, now) => {
      await db.conversations.put(
        provisionalConversation(provisionalId, Conversation_Kind.GROUP, input.title, now),
      );
    },
  );
  return { ...enqueued, conversationId: provisionalId };
}

export async function enqueueCreateChannel(input: {
  title: string;
  publicUsername: string | null;
  originDeviceId: Hex;
}): Promise<EnqueuedOperation & { conversationId: Hex }> {
  const provisionalId = bytesToHex(randomBytes(16));
  const enqueued = await enqueue(
    {
      conversationId: provisionalId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP4.METHOD_ID_CREATE_CHANNEL,
      normalizedPayload: normalizeCreateChannel(input.title, input.publicUsername),
    },
    async (_opId, _tempId, now) => {
      const record = provisionalConversation(
        provisionalId,
        Conversation_Kind.CHANNEL,
        input.title,
        now,
      );
      await db.conversations.put({
        ...record,
        publicUsername: input.publicUsername,
        subscribed: true,
      });
    },
  );
  return { ...enqueued, conversationId: provisionalId };
}

export async function enqueueCreateInvite(input: {
  conversationId: Hex;
  originDeviceId: Hex;
  maxUses: number;
  expiresAt: number;
  approval: boolean;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_CREATE_INVITE,
    normalizedPayload: normalizeCreateInvite(
      input.conversationId,
      input.maxUses,
      input.expiresAt,
      input.approval,
    ),
  });
}

export async function enqueueRevokeInvite(input: {
  conversationId: Hex;
  originDeviceId: Hex;
  token: string;
  tokenHash: Hex;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP3.METHOD_ID_REVOKE_INVITE,
      normalizedPayload: normalizeRevokeInvite(input.conversationId, input.tokenHash),
    },
    async () => {
      // The local copy goes immediately. Section 06 makes revocation effective
      // at the server's transaction boundary, so keeping a link on screen that
      // may already be dead would be worse than removing one that is briefly
      // still usable.
      await db.invites.delete([input.conversationId, input.token]);
    },
  );
}

export async function enqueueJoinConversation(input: {
  token: string | null;
  publicUsername: string | null;
  originDeviceId: Hex;
}): Promise<EnqueuedOperation & { conversationId: Hex }> {
  // A join has no conversation identifier until the server answers, so it is
  // scheduled under a provisional one exactly like a creation. No provisional
  // conversation row is written: the user has not been admitted yet, and
  // showing a dialog for a group that may refuse them would be a lie.
  const provisionalId = bytesToHex(randomBytes(16));
  const enqueued = await enqueue({
    conversationId: provisionalId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_JOIN_CONVERSATION,
    normalizedPayload: normalizeJoinConversation(input.token, input.publicUsername),
  });
  return { ...enqueued, conversationId: provisionalId };
}

export async function enqueueLeaveConversation(input: {
  conversationId: Hex;
  originDeviceId: Hex;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_LEAVE_CONVERSATION,
    normalizedPayload: normalizeLeaveConversation(input.conversationId),
  });
}

export async function enqueueSetMemberRole(input: {
  conversationId: Hex;
  userId: Hex;
  originDeviceId: Hex;
  role: string;
  permissions: number;
  expectedPolicyVersion: number;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_SET_MEMBER_ROLE,
    normalizedPayload: normalizeSetMemberRole(
      input.conversationId,
      input.userId,
      input.role,
      input.permissions,
      input.expectedPolicyVersion,
    ),
  });
}

export async function enqueueSetBan(input: {
  conversationId: Hex;
  userId: Hex;
  originDeviceId: Hex;
  banned: boolean;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_SET_BAN,
    normalizedPayload: normalizeSetBan(input.conversationId, input.userId, input.banned),
  });
}

export async function enqueueUpsertTopic(input: {
  conversationId: Hex;
  topicId: Hex | null;
  originDeviceId: Hex;
  title: string;
  closed: boolean;
  expectedVersion: number;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_UPSERT_TOPIC,
    normalizedPayload: normalizeUpsertTopic(
      input.conversationId,
      input.topicId,
      input.title,
      input.closed,
      input.expectedVersion,
    ),
  });
}

export async function enqueueDeleteTopic(input: {
  conversationId: Hex;
  topicId: Hex;
  originDeviceId: Hex;
  expectedVersion: number;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_DELETE_TOPIC,
    normalizedPayload: normalizeDeleteTopic(
      input.conversationId,
      input.topicId,
      input.expectedVersion,
    ),
  });
}

export async function enqueueCreatePoll(input: {
  conversationId: Hex;
  originDeviceId: Hex;
  question: string;
  options: string[];
  multiple: boolean;
  anonymous: boolean;
  correctIndex: number | null;
  explanation: string | null;
  closesAt: number | null;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_CREATE_POLL,
    normalizedPayload: normalizeCreatePoll(input),
  });
}

/**
 * Casts or changes a vote.
 *
 * The optimistic update writes `myVotes` but never touches the counts. A vote
 * change removes an earlier vote as well as adding a new one, and guessing the
 * arithmetic locally would show a total that briefly disagrees with every other
 * participant's screen; the authoritative `Poll` comes back on the response and
 * through POLL_CHANGED.
 */
export async function enqueueVote(input: {
  conversationId: Hex;
  pollId: Hex;
  optionIds: Hex[];
  originDeviceId: Hex;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP3.METHOD_ID_VOTE,
      normalizedPayload: normalizeVote(input.conversationId, input.pollId, input.optionIds),
    },
    async () => {
      const poll = await db.polls.get(input.pollId);
      if (poll) {
        await db.polls.put({ ...poll, myVotes: [...input.optionIds] });
      }
    },
  );
}

export async function enqueueClosePoll(input: {
  conversationId: Hex;
  pollId: Hex;
  originDeviceId: Hex;
  expectedVersion: number;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_CLOSE_POLL,
    normalizedPayload: normalizeClosePoll(input.conversationId, input.pollId, input.expectedVersion),
  });
}

export async function enqueueResolveJoinRequest(input: {
  conversationId: Hex;
  requesterId: Hex;
  originDeviceId: Hex;
  approve: boolean;
}): Promise<EnqueuedOperation> {
  return enqueue({
    conversationId: input.conversationId,
    originDeviceId: input.originDeviceId,
    methodId: MethodIdP3.METHOD_ID_RESOLVE_JOIN_REQUEST,
    normalizedPayload: normalizeResolveJoinRequest(
      input.conversationId,
      input.requesterId,
      input.approve,
    ),
  });
}

export async function enqueueSetConversationPolicy(input: {
  conversationId: Hex;
  originDeviceId: Hex;
  expectedVersion: number;
  slowModeSeconds: number;
  protectedContent: boolean;
  historyPolicy: string;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP3.METHOD_ID_SET_CONVERSATION_POLICY,
      normalizedPayload: normalizeSetConversationPolicy(input),
    },
    async () => {
      const conversation = await db.conversations.get(input.conversationId);
      if (conversation) {
        await db.conversations.put({
          ...conversation,
          slowModeSeconds: input.slowModeSeconds,
          protectedContent: input.protectedContent,
        });
      }
    },
  );
}

export async function enqueueUpdateConversation(input: {
  conversationId: Hex;
  originDeviceId: Hex;
  title: string;
  publicUsername: string | null;
  expectedVersion: number;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP3.METHOD_ID_UPDATE_CONVERSATION,
      normalizedPayload: normalizeUpdateConversation(
        input.conversationId,
        input.title,
        input.publicUsername,
        input.expectedVersion,
      ),
    },
    async () => {
      const conversation = await db.conversations.get(input.conversationId);
      if (conversation) {
        await db.conversations.put({ ...conversation, title: input.title });
      }
    },
  );
}

// ---------------------------------------------------------------------------
// Phase 4 — channels
// ---------------------------------------------------------------------------

export async function enqueueSubscribeChannel(input: {
  conversationId: Hex;
  originDeviceId: Hex;
  inviteToken: string | null;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP4.METHOD_ID_SUBSCRIBE_CHANNEL,
      normalizedPayload: normalizeSubscribeChannel(input.conversationId, input.inviteToken),
    },
    async () => {
      const conversation = await db.conversations.get(input.conversationId);
      if (conversation) {
        await db.conversations.put({ ...conversation, subscribed: true });
      }
    },
  );
}

export async function enqueueUnsubscribeChannel(input: {
  conversationId: Hex;
  originDeviceId: Hex;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP4.METHOD_ID_UNSUBSCRIBE_CHANNEL,
      normalizedPayload: normalizeUnsubscribeChannel(input.conversationId),
    },
    async () => {
      const conversation = await db.conversations.get(input.conversationId);
      if (conversation) {
        await db.conversations.put({ ...conversation, subscribed: false });
      }
    },
  );
}

export async function enqueuePublishPost(input: {
  conversationId: Hex;
  senderId: Hex;
  originDeviceId: Hex;
  text: string;
  silent: boolean;
  attachments?: StoredAttachment[];
  attachmentKinds?: string[];
}): Promise<EnqueuedOperation> {
  const attachments = input.attachments ?? [];
  const content: StoredContent = {
    ...textContent(input.text, null, null),
    ...(attachments.length > 0
      ? { kind: attachmentContentKind(input.attachmentKinds ?? []), attachments }
      : {}),
  };

  return enqueue(
    {
      conversationId: input.conversationId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP4.METHOD_ID_PUBLISH_POST,
      normalizedPayload: normalizePublishPost(input.conversationId, content, input.silent),
      attachments,
    },
    async (opId, tempId, now) => {
      // A post is an ordinary message locally: the same optimistic bubble, the
      // same reconciliation. Section 15 uses the same message transaction on
      // the server and adds only notification metadata.
      await db.messages.add({
        conversationId: input.conversationId,
        id: tempId,
        orderSeq: PENDING_ORDER_BASE + now,
        createdSeq: 0,
        senderId: input.senderId,
        sender: { kind: 'USER' as const, id: input.senderId, displayName: '', avatarMediaId: null, profileVersion: 0 },
        version: 1,
        content,
        createdAt: now,
        editedAt: null,
        deleted: false,
        outgoing: true,
        localState: 'PENDING',
        opId,
        tempId,
        forwardOrigin: null,
        viewCount: 0,
        pinned: false,
      });
      const conversation = await db.conversations.get(input.conversationId);
      if (conversation) {
        await db.conversations.put({
          ...conversation,
          updatedAt: now,
          lastMessageAt: now,
          lastMessagePreview: content.text,
          lastOwnSendAt: now,
        });
      }
      await db.drafts.delete(input.conversationId);
    },
  );
}

export async function enqueueLinkDiscussion(input: {
  channelId: Hex;
  groupId: Hex | null;
  originDeviceId: Hex;
}): Promise<EnqueuedOperation> {
  return enqueue(
    {
      conversationId: input.channelId,
      originDeviceId: input.originDeviceId,
      methodId: MethodIdP4.METHOD_ID_LINK_DISCUSSION,
      normalizedPayload: normalizeLinkDiscussion(input.channelId, input.groupId),
    },
    async () => {
      const conversation = await db.conversations.get(input.channelId);
      if (conversation) {
        await db.conversations.put({ ...conversation, linkedDiscussionId: input.groupId });
      }
    },
  );
}

/**
 * The channel notification choice.
 *
 * Section 06 makes this a local preference expressed to the notification
 * router; it is not one of the fixed protocol methods, so it is stored on the
 * conversation row and applied when notifications are delivered.
 */
export async function setNotificationMode(
  conversationId: Hex,
  mode: NotificationMode,
): Promise<void> {
  await persist(async () => {
    await db.dexie.transaction('rw', [db.conversations], async () => {
      const conversation = await db.conversations.get(conversationId);
      if (conversation) {
        await db.conversations.put({ ...conversation, notificationMode: mode });
      }
    });
  });
}

/**
 * Records that a post was seen, locally.
 *
 * Section 06 deduplicates views by account, post and day. The client keeps the
 * same day-scoped key so that scrolling a channel back and forth does not send
 * the same view a hundred times; the server deduplicates authoritatively.
 */
const viewedToday = new Set<string>();
let viewedDay = -1;

export function shouldRecordView(conversationId: Hex, messageId: string): boolean {
  const day = Math.floor(Date.now() / 86_400_000);
  if (day !== viewedDay) {
    // The dedup window is one day, so yesterday's keys are not merely useless —
    // keeping them would suppress a view the server expects today, and would
    // grow without bound in a session left open for weeks.
    viewedToday.clear();
    viewedDay = day;
  }
  const key = `${conversationId}:${messageId}`;
  if (viewedToday.has(key)) {
    return false;
  }
  viewedToday.add(key);
  return true;
}

/** The private self-conversation, if the server has told us about it. */
export async function getSavedMessages(): Promise<ConversationRecord | null> {
  const rows = await db.conversations.toArray();
  return rows.find((row) => row.kind === Conversation_Kind.SAVED) ?? null;
}

// ---------------------------------------------------------------------------
// User-initiated recovery
// ---------------------------------------------------------------------------

/**
 * Puts a failed operation back in the queue under its original identifier.
 *
 * Section 05 forbids *automatic* retries of terminal permission and content
 * errors; it does not forbid the user asking again, and Section 14 requires the
 * error icon to offer exactly that. The identifier is preserved because the
 * intent has not changed — a changed intent needs a new operation, which is
 * what `enqueueSendMessage` produces after a discard.
 */
export async function retryOperation(opId: Hex): Promise<void> {
  await persist(async () => {
    await db.dexie.transaction('rw', [db.outbox, db.messages], async () => {
      const record = await db.outbox.get(opId);
      if (!record || record.state !== 'FAILED') {
        return;
      }
      await db.outbox.put({
        ...record,
        state: 'QUEUED',
        nextAttemptAt: Date.now(),
        lastError: null,
      });
      const message = await db.messages.where('opId').equals(opId).first();
      if (message) {
        await db.messages.put({ ...message, localState: 'PENDING' });
      }
    });
  });
}

/** Removes a failed operation and the bubble it produced. */
export async function discardOperation(opId: Hex): Promise<void> {
  await persist(async () => {
    await db.dexie.transaction('rw', [db.outbox, db.messages], async () => {
      const message = await db.messages.where('opId').equals(opId).first();
      if (message) {
        await db.messages.delete([message.conversationId, message.id]);
      }
      await db.outbox.delete(opId);
    });
  });
}

/**
 * Clears account-scoped local data on sign-out.
 *
 * The caller warns first when unsent work would be lost; Section 14 requires
 * that warning and the option to cancel, so this function only ever runs after
 * the user has chosen to proceed.
 */
export async function clearLocalAccountData(): Promise<void> {
  await db.dexie.transaction(
    'rw',
    [
      db.accounts,
      db.conversations,
      db.messages,
      db.eventsSeen,
      db.streamCursors,
      db.outbox,
      db.drafts,
      db.uploads,
      db.media,
      db.downloadParts,
      db.reactions,
      db.pins,
      db.members,
      db.topics,
      db.polls,
      db.invites,
    ],
    async () => {
      await Promise.all([
        db.accounts.clear(),
        db.conversations.clear(),
        db.messages.clear(),
        db.eventsSeen.clear(),
        db.streamCursors.clear(),
        db.outbox.clear(),
        db.drafts.clear(),
        db.uploads.clear(),
        db.media.clear(),
        db.downloadParts.clear(),
        db.reactions.clear(),
        db.pins.clear(),
        db.members.clear(),
        db.topics.clear(),
        db.polls.clear(),
        db.invites.clear(),
      ]);
    },
  );
  // The installation identifier deliberately survives: it identifies the
  // browser profile, not the session, and rotating it would present the next
  // sign-in as an unrelated device.
}
